import * as health from "../../lib/api/health";
import * as posActions from "../../lib/posActions";
import * as posModels from "../../lib/api/posModels";
import * as routeHelpers from "../../lib/routeHelpers";
import * as settings from "../../lib/api/settings";
import cn from "classnames";
import describeNetworkError from "../../lib/describeNetworkError";
import ErrorModal from "../ErrorModal";
import HealthCheckModal from "../HealthCheckModal";
import logger from "../../lib/sentryLogger";
import ManagerHeader from "../ManagerHeader";
import OutlineButton from "../OutlineButton";
import React, {useContext, useEffect, useRef, useState} from "react";
import styles from "./ManagerSettings.module.css";
import ToggleButton from "../ToggleButton";
import {API, Actions} from "habit-core";
import {StationContext} from "../StationContext";
import {useAppSelector, useAppDispatch} from "../../lib/hooks";
import {useNavigate} from "react-router-dom";
import {TillAssignmentContext} from "../TillAssignmentContext";
import TextInput from "../TextInput";
import StyledKeyboard from "../StyledKeyboard";
import Spinner from "../Spinner";

const strings = {
    title: "Settings",
    stationMode: "Station Mode",
    laneAssignment: "Lane Assignment",
    save: "Save",
    healthCheckSectionTitle: "Health Check",
    runHealthCheck: "Run Health Check",
    willRequireTill:
        "To change the station mode, you will be required to assign a till.",
    enableCCPayment: "Enable CC Payment",
    stationModeChangeError:
        "An unexpected error occurred when recording the station mode change. Please try again.",
    kioskCashHeader: "Kiosk Cash Payment Pager Number",
    enterNumber: "Enter Number",
    autoPrintReceipts: "Automatically Print Receipts",
    yes: "Yes",
    no: "No",
    errorMessage: "Failed to save the following settings:",
};

const MODES: {label: posModels.StationMode; value: string}[] = [
    {label: "front_of_house_1", value: "Front of House 1"},
    {label: "front_of_house_2", value: "Front of House 2"},
    {label: "line_buster", value: "Line Buster"},
    {label: "drive_thru_order_taker", value: "Drive Thru Order Taker"},
    {
        label: "drive_thru_order_fulfillment",
        value: "Drive Thru Order Fulfillment",
    },
];

const LANE_OPTIONS: {value: posModels.LaneAssignment; label: string}[] = [
    {value: "lane_1", label: "Lane 1"},
    {value: "lane_2", label: "Lane 2"},
    {value: "both", label: "Both"},
];

export default function ManagerSettings() {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const {tillAssignmentSkipped, setTillAssignmentSkipped} = useContext(
        TillAssignmentContext,
    );
    const storeId = useAppSelector((state) => state.currentOrder.storeId);
    const currentOrder = useAppSelector((state) => state.pos.currentOrder);
    const isCurrentOrderPending = useAppSelector(
        (state) => !!state.pos.pendingOrders.byId[currentOrder.id ?? ""],
    );
    const currentStationMode = useAppSelector(
        (state) => state.pos.station.mode,
    );
    const currentLaneAssignment = useAppSelector(
        (state) => state.pos.station.laneAssignment,
    );
    const initialAutoPrintReceipts = useAppSelector(
        (state) => state.pos.station.autoPrintReceipts,
    );
    const [autoPrintReceipts, setAutoPrintReceipts] = useState(
        initialAutoPrintReceipts,
    );
    const {
        setCashierMode,
        isTraining,
        isCCPaymentDisabled,
        setIsCCPaymentDisabled,
    } = useContext(StationContext);
    const [selectedStationMode, setSelectedStationMode] =
        useState(currentStationMode);
    const [selectedLaneAssignment, setSelectedLaneAssignment] =
        useState<posModels.LaneAssignment>(currentLaneAssignment ?? "lane_1");
    const [isStationModeUpdated, setIsStationModeUpdated] = useState(false);

    const [healthCheckData, setHealthCheckData] = useState<
        posModels.ExhaustiveHealthCheckResponse | undefined
    >(undefined);
    const [healthCheckLoading, setHealthCheckLoading] = useState(false);
    const [healthCheckError, setHealthCheckError] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [saving, setSaving] = useState(false);

    const runHealthCheck = () => {
        setHealthCheckLoading(true);
        health
            .getExhaustiveHealth()
            .then((data) => {
                setHealthCheckData(data);
            })
            .catch((err) => {
                logger.error("Failed to make exhaustive health check.");
                setHealthCheckError(describeNetworkError(err).join("\n"));
            })
            .finally(() => {
                setHealthCheckLoading(false);
            });
    };
    const header = (
        <ManagerHeader
            label={strings.title}
            buttonLabel={strings.runHealthCheck}
            buttonOnClick={runHealthCheck}
            buttonLoading={healthCheckLoading}
        />
    );

    useEffect(() => {
        if (
            selectedStationMode !== currentStationMode ||
            selectedLaneAssignment !== currentLaneAssignment
        ) {
            setIsStationModeUpdated(true);
        } else {
            setIsStationModeUpdated(false);
        }
    }, [
        selectedStationMode,
        currentStationMode,
        selectedLaneAssignment,
        currentLaneAssignment,
    ]);

    const [initialKioskPagerNumber, setInitialKioskPagerNumber] = useState("");
    const [kioskPagerNumber, setKioskPagerNumber] = useState("");
    const [kioskLoading, setKioskLoading] = useState(false);

    useEffect(() => {
        setKioskLoading(true);
        settings
            .getKioskCash()
            .then((data) => {
                const pagerNumberStr = data.pagerNumber
                    ? data.pagerNumber.toString()
                    : "";
                setInitialKioskPagerNumber(pagerNumberStr);
                setKioskPagerNumber(pagerNumberStr);
            })
            .catch((err) => {
                const description = describeNetworkError(err).join("\n");
                logger.error("Failed to get kiosk cash settings.", {
                    errorMessage: description,
                });

                setErrorMessage(description);
            })
            .finally(() => setKioskLoading(false));
    }, []);

    /* keyboard state */
    const [keyboardCollapsed, setKeyboardCollapsed] = useState(true);
    const kioskPagerNumberInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (kioskPagerNumberInputRef.current && !keyboardCollapsed) {
            if (document.activeElement === kioskPagerNumberInputRef.current) {
                kioskPagerNumberInputRef?.current?.scrollIntoView();
            }
        }
    }, [keyboardCollapsed]);

    const onChangeKioskPagerNumber = (input: string) => {
        setKioskPagerNumber(input);
    };

    function enableCCPayment() {
        setIsCCPaymentDisabled(false);
        setCashierMode({type: "user"});
        navigate(routeHelpers.deviceSetup());
    }

    function onSave() {
        setSaving(true);

        const hasKioskPagerChanges =
            kioskPagerNumber !== initialKioskPagerNumber;
        const hasAutoPrintChanges =
            initialAutoPrintReceipts !== autoPrintReceipts;
        const autoPrintPromise = hasAutoPrintChanges
            ? dispatch(posActions.editAutoPrintReceipts(autoPrintReceipts))
            : Promise.resolve();
        const kioskPagerPromise = hasKioskPagerChanges
            ? settings.setKioskCash(
                  kioskPagerNumber ? parseInt(kioskPagerNumber) : null,
              )
            : Promise.resolve();
        const finalLaneAssignment =
            selectedStationMode === "drive_thru_order_taker" ||
            selectedStationMode === "line_buster"
                ? selectedLaneAssignment
                : null;
        const stationModePromise = isStationModeUpdated
            ? dispatch(
                  posActions.editStationMode(
                      selectedStationMode,
                      finalLaneAssignment,
                  ),
              )
            : Promise.resolve();

        Promise.allSettled([
            autoPrintPromise,
            kioskPagerPromise,
            stationModePromise,
        ])
            .then(([autoPrintRes, kioskPagerRes, stationModeRes]) => {
                if (
                    autoPrintRes.status === "rejected" ||
                    kioskPagerRes.status === "rejected" ||
                    stationModeRes.status === "rejected"
                ) {
                    let errorMessages = "";
                    if (autoPrintRes.status === "rejected") {
                        logger.error("Failed to save auto print receipts", {
                            errorMessage: autoPrintRes.reason,
                        });
                        errorMessages = errorMessages.concat(
                            "\n",
                            strings.autoPrintReceipts,
                            `: ${autoPrintRes.reason}`,
                        );
                    }
                    if (kioskPagerRes.status === "rejected") {
                        logger.error("Failed to set kiosk cash settings", {
                            errorMessage: kioskPagerRes.reason,
                        });
                        errorMessages = errorMessages.concat(
                            "\n",
                            strings.kioskCashHeader,
                            `: ${kioskPagerRes.reason}`,
                        );
                    }
                    if (stationModeRes.status === "rejected") {
                        logger.error("Failed to edit station mode", {
                            errorMessage: stationModeRes.reason,
                        });
                        errorMessages = errorMessages.concat(
                            "\n",
                            strings.stationMode,
                            `: ${stationModeRes.reason}`,
                        );
                    }
                    setErrorMessage(`${strings.errorMessage}${errorMessages}`);
                }

                if (stationModeRes.status === "fulfilled") {
                    if (
                        (currentStationMode === "drive_thru_order_taker" ||
                            currentStationMode === "line_buster") &&
                        selectedStationMode !== "drive_thru_order_taker" &&
                        selectedStationMode !== "line_buster"
                    ) {
                        setTillAssignmentSkipped(false);
                    }

                    const newOrderType: API.models.OrderType =
                        selectedStationMode === "front_of_house_1" ||
                        selectedStationMode === "front_of_house_2"
                            ? "in_store"
                            : "drive_thru";

                    let newOrderTypeSet = false;

                    if (
                        selectedStationMode !== "drive_thru_order_fulfillment"
                    ) {
                        if (isCurrentOrderPending) {
                            newOrderTypeSet = true;
                            dispatch(
                                posActions.clearCurrentOrder(
                                    storeId ?? "",
                                    newOrderType,
                                ),
                            ).then(() => {
                                dispatch(
                                    posActions.reserveCurrentOrderId(
                                        isTraining,
                                    ),
                                );
                            });
                        } else if (!currentOrder.id) {
                            dispatch(
                                posActions.reserveCurrentOrderId(isTraining),
                            );
                        }
                    }

                    if (!newOrderTypeSet) {
                        dispatch(
                            Actions.currentOrderActions.selectOrderType(
                                newOrderType,
                            ),
                        );
                    }
                }

                if (
                    autoPrintRes.status === "fulfilled" &&
                    kioskPagerRes.status === "fulfilled" &&
                    stationModeRes.status === "fulfilled"
                ) {
                    navigate(routeHelpers.deviceSetup(), {
                        state: {
                            skipCCRegistrationCheck:
                                selectedStationMode ===
                                    "drive_thru_order_taker" ||
                                selectedStationMode === "line_buster",
                        },
                    });
                    setCashierMode({type: "user"});
                }
            })
            .finally(() => {
                setSaving(false);
            });
    }

    return (
        <>
            <div className={styles.container}>
                {header}
                <div className={styles.content}>
                    <div className={styles.section}>
                        <div className={styles.text}>{strings.stationMode}</div>
                        <div className={styles.modes}>
                            {MODES.map((m, i) => (
                                <ToggleButton
                                    key={i}
                                    className={styles.mode}
                                    label={m.value}
                                    checked={selectedStationMode === m.label}
                                    onChange={() =>
                                        setSelectedStationMode(m.label)
                                    }
                                />
                            ))}
                        </div>
                        {(currentStationMode === "drive_thru_order_taker" ||
                            currentStationMode === "line_buster") &&
                        tillAssignmentSkipped ? (
                            <div className={styles.text}>
                                {strings.willRequireTill}
                            </div>
                        ) : null}
                        {isCCPaymentDisabled ? (
                            <OutlineButton
                                label={strings.enableCCPayment}
                                onClick={enableCCPayment}
                            />
                        ) : null}
                        <div>
                            <div className={styles.text}>
                                {strings.kioskCashHeader}
                            </div>
                            {kioskLoading ? (
                                <div className={styles.kioskLoading}>
                                    <Spinner isSmall />
                                </div>
                            ) : (
                                <TextInput
                                    disabled={saving}
                                    value={kioskPagerNumber}
                                    onChange={(e) => {
                                        const str = e.target.value;
                                        const val = str.replace(/[^\d]/g, "");
                                        onChangeKioskPagerNumber(val);
                                    }}
                                    placeholder={strings.enterNumber}
                                    onFocus={() => setKeyboardCollapsed(false)}
                                    ref={kioskPagerNumberInputRef}
                                    inputMode="none"
                                />
                            )}
                        </div>
                        <div className={styles.autoPrintReceiptsSection}>
                            <div className={styles.text}>
                                {strings.autoPrintReceipts}
                            </div>
                            <div className={styles.autoPrintOptions}>
                                <ToggleButton
                                    className={styles.autoPrintButton}
                                    label={strings.yes}
                                    checked={autoPrintReceipts}
                                    onChange={() => setAutoPrintReceipts(true)}
                                    disabled={saving}
                                />
                                <ToggleButton
                                    className={styles.autoPrintButton}
                                    label={strings.no}
                                    checked={!autoPrintReceipts}
                                    onChange={() => setAutoPrintReceipts(false)}
                                    disabled={saving}
                                />
                            </div>
                        </div>
                    </div>
                    {selectedStationMode === "drive_thru_order_taker" ||
                    selectedStationMode === "line_buster" ? (
                        <div className={styles.section}>
                            <div className={styles.text}>
                                {strings.laneAssignment}
                            </div>
                            <div className={styles.laneAssignments}>
                                {LANE_OPTIONS.map((l) => (
                                    <ToggleButton
                                        key={l.value}
                                        className={styles.laneAssignment}
                                        label={l.label}
                                        checked={
                                            selectedLaneAssignment === l.value
                                        }
                                        onChange={() =>
                                            setSelectedLaneAssignment(l.value)
                                        }
                                    />
                                ))}
                            </div>
                        </div>
                    ) : null}
                </div>
                <div className={styles.footer}>
                    <OutlineButton
                        className={styles.footerButton}
                        label={strings.save}
                        disabled={
                            !isStationModeUpdated &&
                            kioskPagerNumber === initialKioskPagerNumber
                        }
                        onClick={onSave}
                        loading={saving}
                    />
                </div>
                <div
                    className={cn(
                        styles.keyboardContainer,
                        keyboardCollapsed && styles.keyboardContainerCollapsed,
                    )}>
                    <StyledKeyboard
                        currentInput={kioskPagerNumber}
                        visible={!keyboardCollapsed}
                        setVisible={(val: boolean) =>
                            setKeyboardCollapsed(!val)
                        }
                        onChange={onChangeKioskPagerNumber}
                        inputRefs={[kioskPagerNumberInputRef]}
                        layout="numeric"
                        onPressEnter={onSave}
                    />
                </div>
            </div>
            {healthCheckData ? (
                <HealthCheckModal
                    healthCheckData={healthCheckData}
                    onClose={() => setHealthCheckData(undefined)}
                />
            ) : null}
            {healthCheckError ? (
                <ErrorModal
                    title="Health Check Error"
                    errorMessage={healthCheckError}
                    showITInfo
                    onClose={() => setHealthCheckError("")}
                />
            ) : null}
            {errorMessage ? (
                <ErrorModal
                    errorMessage={errorMessage}
                    showITInfo
                    onClose={() => setErrorMessage("")}
                />
            ) : null}
        </>
    );
}
